import Icon from '$store/components/ui/Icon.tsx'
import { formatPrice } from '$store/sdk/format.ts'
import { useSignal } from '@preact/signals'
import type { FilterToggle } from 'apps/commerce/types.ts'
import fzf from 'site/sdk/fzf.ts'
import { matchCanonical } from '$store/utils/matchCanonical.ts'

export interface MatcherFilters {
    /**
     * @title Expressão Regex
     * @description Expressão Regex para esconder o titulo em uma url. Ex: Use /feminino/* para esconder o titulo na categoria Feminino.
     * @default *
     */
    matcher: string
    /**
     * @title Nomes das Categorias  a ser escondida.
     * @titleBy label
     */
    filtersLabelsToHide: string[]
}

const matchersFiltersToHide = (matchers: Array<MatcherFilters> | undefined) => {
    const path = globalThis.location.pathname
    if (!path || !matchers || !matchers.length) return

    const matcheredFilterToHide = matchers?.filter((item) => item.filtersLabelsToHide.length > 0)
        .filter((item) => matchCanonical(item.matcher, path)).flatMap((item) =>
            item.filtersLabelsToHide
        )
    return matcheredFilterToHide
}

const VALUES_TO_HIDE = ['looks-torra']

export const FilterValues = ({
    values,
    placeholder,
    label: filterLabel,
    key,
    matchersFilters,
}: FilterToggle & {
    placeholder?: string
    isDepartament?: boolean
    matchersFilters?: MatcherFilters[]
}) => {
    const search = useSignal('')
    const filteredValues = values.filter((item) => fzf(search.value, item.label.toLowerCase()))
    const hideMatchedFilters = matchersFiltersToHide(matchersFilters)
    const filtersToHide = filteredValues.filter((item) => !hideMatchedFilters?.includes(item.label))
    const filtersMatched = filterLabel === 'Categoria' ? filtersToHide : filteredValues

    if (filterLabel === 'Preço') {
        values.sort((a, b) => {
            const [deA] = a.label.split(':')
            const [deB] = b.label.split(':')

            return Number(deA) - Number(deB)
        })
    }

    return (
        <div class='pb-7'>
            {placeholder && (
                <div class='border border-neutral-4 bg-[#FAFAFA] h-9 rounded-full w-full mb-4 flex items-center relative'>
                    <input
                        type='text'
                        placeholder={placeholder}
                        value={search.value}
                        onInput={(e) => {
                            search.value = (e.target as HTMLInputElement).value
                        }}
                        class='outline-none w-full px-4 placeholder:font-lato placeholder:text-xs mb-1 placeholder:text-neutral-2 bg-transparent'
                    />
                    <Icon
                        class='text-primary-700 absolute right-3 top-1/2 -translate-y-1/2'
                        id='MagnifyingGlass'
                        width={16}
                        height={16}
                        strokeWidth={0.01}
                    />
                </div>
            )}
            {filterLabel === 'Tamanho'
                ? (
                    <ul class='flex flex-wrap gap-2'>
                        {values.map(({ label, url, selected, value }, index) => {
                            if (VALUES_TO_HIDE.includes(value)) {
                                return null
                            }

                            return (
                                <span
                                    role='link'
                                    data-selected={selected}
                                    data-filtering={search.value !== ''}
                                    data-filtered={label
                                        .toLowerCase()
                                        .includes(search.value.toLowerCase())}
                                    data-insights-filter={`${key}:${value}`}
                                    class={` cursor-pointer flex justify-center items-center min-w-[32px] h-8 group data-[filtering=true]:data-[filtered=false]:opacity-10 border border-grey-200 hover:bg-grey-100 transition-all data-[selected=true]:bg-primary-700 text-neutral-1 data-[selected=true]:text-white relative filter-${key}-${index}`}
                                    title={label}
                                    onClick={function (_event) {
                                        location.href = url
                                        const $elements = document.querySelectorAll<HTMLElement>(
                                            `.filter-${key}-${index}`,
                                        )
                                        $elements.forEach(($element) => {
                                            $element.dataset.loading = ''
                                        })
                                    }}
                                >
                                    <span class=' text-sm text-center font-lato grid place-content-center p-2'>
                                        {label}
                                    </span>
                                    <Icon
                                        id='Spinner'
                                        width={20}
                                        height={20}
                                        class='text-black animate-spin opacity-0 group-data-[loading]:opacity-100 absolute'
                                    />
                                </span>
                            )
                        })}
                    </ul>
                )
                : (
                    <div class='relative'>
                        {filteredValues.length > 10 && (
                            <details class='peer absolute bottom-[2px] translate-y-full group/details select-none'>
                                <summary class='text-sm text-primary-700 font-lato cursor-pointer list-none'>
                                    <span class='block group-open/details:hidden'>
                                        Ver mais (+)
                                    </span>
                                    <span class='hidden group-open/details:block'>
                                        Ver menos (-)
                                    </span>
                                </summary>
                            </details>
                        )}
                        <div
                            class={`${
                                filteredValues.length > 10
                                    ? 'h-[366px] peer-open:h-auto overflow-hidden transition-all duration-300 pb-2'
                                    : ''
                            }`}
                        >
                            <ul class='flex flex-wrap flex-col pb-0'>
                                {filtersMatched.map(({ label, url, selected, value }, index) => {
                                    const splitLabel = label.split(':')

                                    const isPrice = filterLabel === 'Preço' &&
                                        splitLabel.length === 2

                                    const content = isPrice
                                        ? `De ${formatPrice(Number(splitLabel[0]))} até ${
                                            formatPrice(
                                                Number(splitLabel[1]),
                                            )
                                        }`
                                        : label

                                    if (VALUES_TO_HIDE.includes(value)) {
                                        return null
                                    }

                                    return (
                                        <li>
                                            <span
                                                role='link'
                                                class={`flex items-center gap-2 group py-2 filter-${key}-${index} cursor-pointer`}
                                                title={label}
                                                data-filtering={search.value !== ''}
                                                data-filtered={label
                                                    .toLowerCase()
                                                    .includes(search.value.toLowerCase())}
                                                data-insights-filter={`${key}:${value}`}
                                                onClick={function (_event) {
                                                    location.href = url
                                                    // event.preventDefault()
                                                    const $elements = document.querySelectorAll<
                                                        HTMLElement
                                                    >(
                                                        `.filter-${key}-${index}`,
                                                    )
                                                    $elements.forEach(($element) => {
                                                        $element.dataset.loading = ''
                                                    })
                                                }}
                                            >
                                                <div
                                                    data-selected={selected}
                                                    data-value={value}
                                                    class='border border-neutral-3 text-black group data-[selected=true]:border-0 data-[selected=true]:bg-primary-700 h-5 w-5 flex justify-center items-center relative'
                                                >
                                                    <Icon
                                                        id='Check'
                                                        width={13}
                                                        height={13}
                                                        class='text-white'
                                                    />
                                                    <Icon
                                                        id='Spinner'
                                                        width={20}
                                                        height={20}
                                                        class='text-black animate-spin opacity-0 group-data-[loading]:opacity-100 absolute'
                                                    />
                                                </div>
                                                <span class='text-sm data-[filtering=true]:data-[filtered=false]:opacity-10'>
                                                    {content}
                                                </span>
                                            </span>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    </div>
                )}
        </div>
    )
}
